import { useAppDispatch, useAppSelector } from '~/store/hooks'
import { updateObjects } from 'store/objects'
import { SELECTED_DOOR_PANEL } from 'store/panel/types'
import Panel from 'components/UIKit/Panel'
import EmptyPanelSection from '../EmptyPanelSection'
import { Door } from '~/store/objects/types'
import { DimensionInput } from '~/ui/DimensionInput'
import { Select, SelectItem } from '~/ui/Select'
import { DoorProduct, doorProducts } from '~/config/doorProducts'
import { Button } from '~/ui/Button'
import { capitalize } from '~/lib/utils'
import { TextField } from '~/ui/TextField'
import { MetadataFields } from '~/components/MetadataFields'

const dockDoorTypes = [
  { label: 'Rollback', value: 'rollbackDoor' },
  { label: 'Roll-up', value: 'rollupDoor' },
  { label: 'Vertical', value: 'verticalDoor' },
]

const SelectedDoorFields = ({ selectedDoors }: { selectedDoors: Door[] }) => {
  const selectedHeights = Array.from(new Set(selectedDoors.map(({ height }) => height)))
  const height = selectedHeights.length === 1 ? selectedHeights[0] : NaN
  const selectedWidths = Array.from(new Set(selectedDoors.map(({ width }) => width)))
  const width = selectedWidths.length === 1 ? selectedWidths[0] : NaN
  const placeholder = 'Multiple values selected'
  const doorType = selectedDoors[0].doorType
  const isSingleDoorSelected = selectedDoors.length === 1
  const selectedDoorProduct = isSingleDoorSelected ? selectedDoors[0].product : undefined
  const selectedDoorProductConfig = doorProducts.find(({ name }) => name === selectedDoorProduct?.name)

  const dispatch = useAppDispatch()

  const handleUpdateDoors = (updates: Partial<Door>) => {
    dispatch(updateObjects(selectedDoors.map(door => ({ ...door, ...updates }))))
  }

  return (
    <div className="flex flex-col gap-y-3 pb-10 m-3">
      <DimensionInput
        placeholder={placeholder}
        label="Height"
        name="height"
        value={height}
        onCommit={distance => handleUpdateDoors({ height: distance || 0 })}
      />
      <DimensionInput
        placeholder={placeholder}
        label="Width"
        name="width"
        value={width}
        onCommit={distance => handleUpdateDoors({ width: distance || 0 })}
      />
      {doorType !== 'manDoor' && (
        <Select
          name="type"
          label="Type"
          placeholder=""
          selectedKey={doorType}
          onSelectionChange={doorType => handleUpdateDoors({ doorType: doorType as string })}
        >
          {dockDoorTypes.map(({ value, label }) => (
            <SelectItem key={label} id={value}>
              {label}
            </SelectItem>
          ))}
        </Select>
      )}
      {selectedDoorProduct && selectedDoorProductConfig ? (
        <div className="flex flex-col gap-3">
          <div className="mt-3 -mx-3 bg-[#F4F4F4] border-y text-xs p-[10px]">Mounted Product</div>
          <p className="italic">Products mounted to doors do not affect the result of a CFD simulation.</p>
          <Select
            name="product"
            label="Product"
            selectedKey={selectedDoorProduct.name}
            onSelectionChange={name => {
              const newProduct = doorProducts.find(p => p.name === name)
              if (!newProduct || !isSingleDoorSelected) return
              const { sizes } = newProduct
              const size = sizes.find(({ label }) => label === selectedDoorProduct.size) ?? sizes[0]
              const voltage = size.voltages.find(voltage => voltage === selectedDoorProduct.voltage) ?? size.voltages[0]
              handleUpdateDoors({
                product: {
                  ...selectedDoorProduct,
                  name: newProduct.name,
                  voltage,
                  size: size.label,
                  orientation: newProduct.orientations[0],
                },
              })
            }}
          >
            {doorProducts.map(product => (
              <SelectItem key={product.name} id={product.name}>
                {product.name}
              </SelectItem>
            ))}
          </Select>
          <Select
            name="size"
            label="Size"
            selectedKey={selectedDoorProduct.size ?? null}
            onSelectionChange={size => {
              const { voltages } = selectedDoorProductConfig.sizes.find(({ label }) => label === size)!
              const voltage = voltages.find(v => v === selectedDoorProduct.voltage) ?? voltages[0]
              handleUpdateDoors({ product: { ...selectedDoorProduct, size: size as string, voltage } })
            }}
          >
            {selectedDoorProductConfig.sizes.map(({ label }) => (
              <SelectItem key={label} id={label}>
                {label}
              </SelectItem>
            ))}
          </Select>
          <Select
            name="voltage"
            label="Voltage"
            selectedKey={selectedDoorProduct.voltage}
            onSelectionChange={voltage =>
              handleUpdateDoors({ product: { ...selectedDoorProduct, voltage: voltage as string } })
            }
          >
            {(
              selectedDoorProductConfig.sizes.find(({ label }) => label === selectedDoorProduct.size) ??
              selectedDoorProductConfig.sizes[0]
            ).voltages.map(v => (
              <SelectItem key={v} id={v}>
                {v}
              </SelectItem>
            ))}
          </Select>
          <Select
            name="orientation"
            label="Orientation"
            selectedKey={selectedDoorProduct.orientation}
            onSelectionChange={orientation => {
              handleUpdateDoors({
                product: { ...selectedDoorProduct, orientation: orientation as DoorProduct['orientations'][number] },
              })
            }}
          >
            {selectedDoorProductConfig.orientations.map(orientation => (
              <SelectItem key={orientation} id={orientation}>
                {capitalize(orientation)}
              </SelectItem>
            ))}
          </Select>
          <TextField
            variant="normal"
            label="Detailed Label"
            value={selectedDoorProduct.label}
            onChange={value => handleUpdateDoors({ product: { ...selectedDoorProduct, label: value } })}
          />
          <Button variant="destructive" onPress={() => handleUpdateDoors({ product: undefined })}>
            Delete Product
          </Button>
          <MetadataFields
            onChange={(fieldName, newValue) => handleUpdateDoors({ metadata: { [fieldName]: newValue } })}
            onDeleteImage={updatedDoor => handleUpdateDoors(updatedDoor)}
            selectedObjects={selectedDoors}
          />
        </div>
      ) : (
        <></>
      )}
    </div>
  )
}

const SelectedDoorPanel = ({ alignment = 'right' }: { alignment?: string }) => {
  const doorModels = useAppSelector(state => state.objects.present.doors)
  const selectedObjects = useAppSelector(state => state.selectedObjects)

  const selectedDoors = selectedObjects.map(({ id }) => doorModels[id]).filter((d): d is Door => !!d)
  const selectedTypes = new Set(selectedDoors.map(({ doorType }) => doorType))
  const isMultipleTypesSelected = selectedTypes.size > 1

  return (
    <Panel docked scrollable title="Door" alignment={alignment} panelKey={SELECTED_DOOR_PANEL}>
      {isMultipleTypesSelected ? (
        <EmptyPanelSection icon="duplicate">Multiple door types selected.</EmptyPanelSection>
      ) : !selectedDoors.length ? (
        <EmptyPanelSection icon="warn">No doors have been selected</EmptyPanelSection>
      ) : (
        <SelectedDoorFields selectedDoors={selectedDoors} />
      )}
    </Panel>
  )
}

export default SelectedDoorPanel
